import { useRef, useEffect } from 'react';

const Game = () => {
  const canvasRef = useRef(null);
  const scoreRef = useRef(0);
  const gameOverRef = useRef(false);
  const obstacleSpeed = useRef(1.5);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const player = { x: 80, y: canvas.height - 30, radius: 15, velocityY: 0, jumping: false };
    const gravity = 0.4;
    const jumpPower = -10;
    const obstacles = [];
    let groundOffset = 0;
    const groundSpeed = 1;
    const clouds = [{ x: 100, y: 50 }, { x: 300, y: 30 }, { x: 500, y: 70 }];
    const cloudSpeed = 0.5;
    let lastScore = 0;

    for (let i = 0; i < 3; i++) {
      obstacles.push({
        x: canvas.width + i * 150,
        y: canvas.height - 20,
        radius: 10,
        speed: obstacleSpeed.current,
        type: 'regular',
      });
    }

    const spawnObstacle = () => {
      const isSpecial = Math.random() < 0.2;
      obstacles.push({
        x: canvas.width,
        y: canvas.height - 20,
        radius: 10,
        speed: obstacleSpeed.current,
        type: isSpecial ? 'special' : 'regular',
      });
    };

    const handleJump = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (!player.jumping && !gameOverRef.current) {
          player.velocityY = jumpPower;
          player.jumping = true;
        } else if (gameOverRef.current) resetGame();
      }
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      if (!player.jumping && !gameOverRef.current) {
        player.velocityY = jumpPower;
        player.jumping = true;
      } else if (gameOverRef.current) resetGame();
    };

    const resetGame = () => {
      scoreRef.current = 0;
      gameOverRef.current = false;
      player.y = canvas.height - 30;
      player.velocityY = 0;
      player.jumping = false;
      obstacles.length = 0;
      obstacleSpeed.current = 1.5;
      for (let i = 0; i < 3; i++) {
        obstacles.push({
          x: canvas.width + i * 150,
          y: canvas.height - 20,
          radius: 10,
          speed: obstacleSpeed.current,
          type: 'regular',
        });
      }
    };

    canvas.setAttribute('tabindex', 0);
    canvas.focus();
    canvas.addEventListener('keydown', handleJump);
    canvas.addEventListener('touchstart', handleTouchStart);

    const gameLoop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#a3bffa';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      groundOffset -= groundSpeed;
      if (groundOffset <= -canvas.width) groundOffset = 0;
      ctx.fillStyle = '#86efac';
      ctx.fillRect(groundOffset, canvas.height - 10, canvas.width, 10);
      ctx.fillRect(groundOffset + canvas.width, canvas.height - 10, canvas.width, 10);

      clouds.forEach(cloud => {
        cloud.x -= cloudSpeed;
        if (cloud.x < -50) cloud.x = canvas.width + 50;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(cloud.x, cloud.y, 20, 0, Math.PI * 2);
        ctx.arc(cloud.x + 20, cloud.y, 20, 0, Math.PI * 2);
        ctx.arc(cloud.x + 40, cloud.y, 20, 0, Math.PI * 2);
        ctx.fill();
      });

      if (!gameOverRef.current) {
        player.velocityY += gravity;
        player.y += player.velocityY;
        if (player.y > canvas.height - 30) {
          player.y = canvas.height - 30;
          player.velocityY = 0;
          player.jumping = false;
        }
        if (obstacles.length === 0 || obstacles[obstacles.length - 1].x < canvas.width - 200) {
          spawnObstacle();
        }
        for (let i = obstacles.length - 1; i >= 0; i--) {
          const obstacle = obstacles[i];
          obstacle.x -= obstacle.speed;
          const dx = player.x - obstacle.x;
          const dy = player.y - obstacle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < player.radius + obstacle.radius) {
            if (obstacle.type === 'special') {
              scoreRef.current += 5;
              obstacles.splice(i, 1);
            } else {
              gameOverRef.current = true;
            }
            break;
          }
          if (obstacle.x + obstacle.radius < 0) {
            obstacles.splice(i, 1);
            scoreRef.current += 1;
          }
        }
        if (scoreRef.current % 10 === 0 && scoreRef.current > lastScore) {
          obstacleSpeed.current += 0.1;
          lastScore = scoreRef.current;
        }
      }

      ctx.fillStyle = '#facc15';
      ctx.beginPath();
      ctx.arc(player.x, player.y, player.radius, 0, Math.PI * 2);
      ctx.fill();
      const eyeRadius = player.radius / 3;
      const pupilRadius = eyeRadius / 2;
      const eyeOffsetX = player.radius / 2;
      const eyeOffsetY = player.radius / 3;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(player.x - eyeOffsetX, player.y - eyeOffsetY, eyeRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(player.x + eyeOffsetX, player.y - eyeOffsetY, eyeRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#000000';
      const pupilOffset = Math.sin(Date.now() / 200) * (eyeRadius - pupilRadius);
      ctx.beginPath();
      ctx.arc(player.x - eyeOffsetX + pupilOffset, player.y - eyeOffsetY, pupilRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(player.x + eyeOffsetX + pupilOffset, player.y - eyeOffsetY, pupilRadius, 0, Math.PI * 2);
      ctx.fill();

      obstacles.forEach(obstacle => {
        if (obstacle.type === 'special') {
          ctx.fillStyle = '#ffd700';
          ctx.beginPath();
          ctx.arc(obstacle.x, obstacle.y, obstacle.radius, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = '#d2b48c';
          ctx.beginPath();
          ctx.arc(obstacle.x, obstacle.y, obstacle.radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#a3bffa';
          ctx.beginPath();
          ctx.arc(obstacle.x, obstacle.y, obstacle.radius / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#f472b6';
          ctx.beginPath();
          ctx.arc(obstacle.x, obstacle.y, obstacle.radius * 0.8, 0, Math.PI * 2);
          ctx.fill();
          const sprinkleCount = 5;
          for (let i = 0; i < sprinkleCount; i++) {
            const angle = (i / sprinkleCount) * Math.PI * 2;
            const sprinkleX = obstacle.x + Math.cos(angle) * (obstacle.radius * 0.6);
            const sprinkleY = obstacle.y + Math.sin(angle) * (obstacle.radius * 0.6);
            ctx.fillStyle = ['#ff0000', '#00ff00', '#0000ff'][i % 3];
            ctx.beginPath();
            ctx.arc(sprinkleX, sprinkleY, 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      });

      ctx.fillStyle = '#000000'; // Black for better contrast
      ctx.font = '24px sans-serif'; // Larger font size
      ctx.fillText(`Score: ${scoreRef.current}`, 20, 40); // Move further from edge

      if (gameOverRef.current) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.fillText('Game Over', canvas.width / 2, canvas.height / 2);
        ctx.fillText('Press Space or Tap to Retry', canvas.width / 2, canvas.height / 2 + 30);
      }

      animationFrameId = requestAnimationFrame(gameLoop);
    };

    gameLoop();

    return () => {
      canvas.removeEventListener('keydown', handleJump);
      canvas.removeEventListener('touchstart', handleTouchStart);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div>
      <canvas ref={canvasRef} width={500} height={150} style={{ maxWidth: '100%' }} />
      <p>Press Space or Tap to jump over obstacles!</p>
    </div>
  );
};

export default Game;